import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface ChartDataPoint {
    label: string;
    value: number;
    secondaryValue?: number; // For comparison bar (e.g., previous year)
    trend?: 'up' | 'down' | 'same';
}

interface BarChartProps {
    title: string;
    dataPoints: ChartDataPoint[];
    primaryLabel?: string;
    secondaryLabel?: string;
    yAxisLabel?: string;
    showTrendMarkers?: boolean;
    height?: number;
    showComparison?: boolean;
    primaryColor?: string;
    secondaryColor?: string;
}

const BarChart: React.FC<BarChartProps> = ({
    title,
    dataPoints,
    primaryLabel = 'Current Year',
    secondaryLabel = 'Previous Year',
    yAxisLabel = 'Percentage',
    showTrendMarkers = true,
    height = 300,
    showComparison = true,
    primaryColor = '#3b82f6', // Blue
    secondaryColor = '#94a3b8' // Gray
}) => {
    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: '#6c757d',
                    font: {
                        size: 12,
                        family: "'Inter', sans-serif"
                    },
                    usePointStyle: true,
                    padding: 20
                }
            },
            title: {
                display: true,
                text: title,
                color: '#2c3e50',
                font: {
                    size: 16,
                    weight: 600,
                    family: "'Inter', sans-serif"
                },
                padding: {
                    top: 10,
                    bottom: 30
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#4a5568',
                borderWidth: 1,
                cornerRadius: 6,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += `${context.parsed.y.toFixed(2)}%`;
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#6c757d',
                    font: {
                        size: 11,
                        family: "'Inter', sans-serif"
                    }
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
                border: {
                    display: false
                },
                ticks: {
                    color: '#6c757d',
                    font: {
                        family: "'Inter', sans-serif"
                    },
                    callback: function (value) {
                        if (typeof value === 'number') {
                            return `${value}%`;
                        }
                        return value;
                    },
                    stepSize: 5, // Show ticks every 5%
                },
                title: {
                    display: true,
                    text: yAxisLabel,
                    color: '#6c757d',
                    font: {
                        size: 12,
                        family: "'Inter', sans-serif"
                    }
                }
            }
        },
        elements: {
            bar: {
                borderRadius: 4,
                borderSkipped: false,
            }
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
    };

    // Prepare chart data with explicit colors
    const chartData: ChartData<'bar'> = {
        labels: dataPoints.map(point => point.label),
        datasets: [
            {
                label: primaryLabel,
                data: dataPoints.map(point => point.value),
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                borderWidth: 1,
                barPercentage: 0.7,
                categoryPercentage: 0.8,
            }
        ]
    };

    // Add secondary dataset if secondary values exist
    const hasSecondaryData = showComparison && dataPoints.some(point => point.secondaryValue !== undefined);
    if (hasSecondaryData) {
        chartData.datasets.push({
            label: secondaryLabel,
            data: dataPoints.map(point => point.secondaryValue || 0),
            backgroundColor: secondaryColor,
            borderColor: secondaryColor,
            borderWidth: 1,
            barPercentage: 0.7,
            categoryPercentage: 0.8,
        });
    }

    // Custom plugins for trend markers (only for primary dataset)
    const trendMarkersPlugin = {
        id: 'trendMarkers',
        afterDatasetsDraw: (chart: any) => {
            if (!showTrendMarkers) return;

            const { ctx, scales, data } = chart;
            const xScale = scales.x;
            const yScale = scales.y;
            const meta = chart.getDatasetMeta(0); // Get metadata for primary dataset

            data.datasets[0].data.forEach((value: number, index: number) => {
                const point = dataPoints[index];
                if (!point.trend || point.trend === 'same') return;

                const bar = meta.data[index];
                if (!bar) return;

                const x = bar.x;
                const barWidth = bar.width;
                const y = yScale.getPixelForValue(value) - 25;

                ctx.save();
                ctx.beginPath();

                // Set color based on trend
                ctx.fillStyle = point.trend === 'up' ? '#ef4444' : '#10b981';
                ctx.font = 'bold 12px "Inter", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Draw trend indicator
                ctx.fillText(point.trend === 'up' ? '↑' : '↓', x, y);
                ctx.restore();
            });
        }
    };

    // Value labels plugin
    const valueLabelsPlugin = {
        id: 'valueLabels',
        afterDatasetsDraw: (chart: any) => {
            const { ctx, scales } = chart;
            const yScale = scales.y;

            chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                const meta = chart.getDatasetMeta(datasetIndex);

                meta.data.forEach((bar: any, index: number) => {
                    const value = dataset.data[index];
                    if (value === 0 || value === null || value === undefined) return;

                    const x = bar.x;
                    const y = bar.y;

                    ctx.save();
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'bottom';
                    ctx.font = 'bold 10px "Inter", sans-serif';

                    // Position label above bar
                    const labelY = y - 8;

                    // Set label color based on dataset
                    ctx.fillStyle = datasetIndex === 0 ? '#2c3e50' : '#6c757d';
                    ctx.fillText(`${value.toFixed(1)}%`, x, labelY);
                    ctx.restore();
                });
            });
        }
    };

    return (
        <div className="chart-container" style={{ height: `${height}px`, position: 'relative' }}>
            <Bar
                data={chartData}
                options={options}
                plugins={[trendMarkersPlugin, valueLabelsPlugin]}
            />
        </div>
    );
};

export default BarChart;